import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from "@rails/request.js"

// Connects to data-controller="note-form"
export default class NoteFormController extends Controller<HTMLFormElement> {
  static targets = ["editor"]
  static classes = ["clean", "dirty", "saving"]
  static values = {
    saveInterval: {
      type: Number,
      default: 3000,
    },
  }

  declare readonly hasEditorTarget: boolean
  declare readonly editorTarget: HTMLElement
  declare readonly cleanClass?: string
  declare readonly dirtyClass?: string
  declare readonly savingClass?: string
  declare readonly saveIntervalValue: number
  #timer?: NodeJS.Timeout

  get editorText() {
    return this.hasEditorTarget ? (this.editorTarget.toString() ?? "") : ""
  }

  get #dirty() {
    return !!this.#timer
  }

  // Lifecycle
  disconnect() {
    this.submit()
  }

  // Actions
  async submit() {
    if (this.#dirty) {
      await this.#save()
    }
  }

  focusEditor() {
    const editor = this.element.querySelector<HTMLElement>(".lexxy-editor__content")
    if (editor) editor.focus()
  }

  change(event: CustomEvent) {
    // @ts-expect-error the form property is not yet typed on some CustomEvent targets
    if (event.target?.form === this.element && !this.#dirty) {
      this.#scheduleSave()
      this.#updateAppearance()
    }
  }

  // Private
  async #save() {
    this.#updateAppearance(true)
    this.#resetTimer()
    await this.#submitForm(this.element)
    this.#updateAppearance()
  }

  #updateAppearance(saving = false) {
    if (this.cleanClass != null) {
      this.element.classList.toggle(this.cleanClass, !this.#dirty)
    }
    if (this.dirtyClass != null) {
      this.element.classList.toggle(this.dirtyClass, this.#dirty)
    }
    if (this.savingClass != null) {
      this.element.classList.toggle(this.savingClass, saving)
    }
  }

  #scheduleSave() {
    this.#timer = setTimeout(() => this.#save(), this.saveIntervalValue)
  }

  #resetTimer() {
    clearTimeout(this.#timer)
    this.#timer = undefined
  }

  async #submitForm(form: HTMLFormElement) {
    const request = new FetchRequest(form.method, form.action, { body: new FormData(form) })

    return await request.perform()
  }
}
